/* eslint-disable sonarjs/cognitive-complexity */
import React, { useEffect } from 'react';
import { InfoIcon, palette, StepStatus, Tooltip } from '@dev/ee-components';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toString, isEmpty, map, uniqueId } from 'lodash-es';
import {
    AddNewCampaignTemplate,
    getCampainCreatorStore,
    StartRemoveAbTabs,
    SetAbRecipients,
    SetAbTabs,
    SetActiveTemplateIndex,
    SetSelectedTemplates,
} from '@/store/campaignCreator/actions';
import { AbTabsType, CampaignErrorNames } from '@/store/campaignCreator/types';
import { getABTestNameByIndex } from '@/services/tools/campaigns';
import { Footer } from '../Footer';
import { SendTo } from '../SendTo';
import { SettingsAndTracking } from '../SettingsAndTracking';
import { useCampaignScreensPathsChecker } from './useCampaignScreensPathsChecker';
import { SubjectAndContent } from '../SubjectAndContent';
import { useGetDefaultSender } from './useGetDefaultSender';
import { SendToStepTitleWrapper } from '../styled';

const MIN_TABS = 2;
const TABS_LIMIT = 7;

const IconWrapper = styled.div`
    display: flex;
    margin-left: 8px;
    svg {
        fill: ${palette.gray[4]};
    }
`;

export const useGetStepsAb = (isAbCampaign: boolean): any[] => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { isDetailsScreen } = useCampaignScreensPathsChecker();
    const defaultSender = useGetDefaultSender();
    const { errors, abTabs, campaign, activeTemplateIndex, isLoading, name } = useSelector(getCampainCreatorStore);

    const tabsLimit =
        isDetailsScreen && !!campaign?.CampaignTemplates ? campaign.CampaignTemplates.length - 1 : TABS_LIMIT;

    const addTab = (): void => {
        const id = abTabs.length;
        const itemTempalte: AbTabsType[] = [
            {
                id: toString(id),
                tabTitle: `${t('version')} ${getABTestNameByIndex(id)}`,
                onDelete: () => dispatch(StartRemoveAbTabs(id)),
                title: t('subjectandcontent'),
                body: <SubjectAndContent index={id} />,
            },
        ];
        dispatch(SetAbTabs(abTabs.concat(itemTempalte)));
        dispatch(AddNewCampaignTemplate(defaultSender.email, defaultSender.name));
        dispatch(SetAbRecipients());
        dispatch(SetActiveTemplateIndex(id));
    };

    useEffect((): void => {
        if (!isAbCampaign) {
            return;
        }
        const defaultAbItems: AbTabsType[] = [
            {
                id: '0',
                tabTitle: `${t('version')} ${getABTestNameByIndex(0)}`,
                onDelete: () => dispatch(StartRemoveAbTabs(0)),
                title: t('subjectandcontent'),
                body: <SubjectAndContent index={0} />,
            },
            {
                id: '1',
                tabTitle: `${t('version')} ${getABTestNameByIndex(1)}`,
                onDelete: () => dispatch(StartRemoveAbTabs(1)),
                title: t('subjectandcontent'),
                body: <SubjectAndContent index={1} />,
            },
        ];

        dispatch(SetAbTabs(defaultAbItems));
        dispatch(SetAbRecipients());
        dispatch(SetSelectedTemplates([]));
    }, []);

    useEffect((): void => {
        if (isAbCampaign && !isEmpty(campaign?.CampaignTemplates) && !!name) {
            const abItems = map(campaign?.CampaignTemplates, (e, index) => ({
                id: `${index}`,
                tabTitle: `${t('version')} ${getABTestNameByIndex(index)}`,
                onDelete: () => dispatch(StartRemoveAbTabs(index)),
                title: t('subjectandcontent'),
                body: <SubjectAndContent index={index} />,
            }));
            dispatch(SetAbTabs(abItems));
        }
    }, [isLoading]);

    const setActiveTemplateIndex = (index: number): void => {
        dispatch(SetActiveTemplateIndex(index));
    };

    if (!isAbCampaign) {
        return [];
    }

    return [
        {
            title: (
                <SendToStepTitleWrapper>
                    {isDetailsScreen ? t('sentto') : t('sendto')}
                    {!isDetailsScreen && (
                        <Tooltip id={`step-1-tooltip`} title={t('sendtocampaigncreator_tooltip')}>
                            <IconWrapper>
                                <InfoIcon />
                            </IconWrapper>
                        </Tooltip>
                    )}
                </SendToStepTitleWrapper>
            ),
            content: <SendTo />,
            status: 'active',
            hasErrors: errors?.[CampaignErrorNames.TARGETS],
        },
        {
            tabs: {
                itemLimit: tabsLimit,
                buttonAddLabel: t('addversion'),
                buttonAddOnClick: () => addTab(),
                removeButton: abTabs.length > MIN_TABS && !isDetailsScreen,
                items: abTabs || [],
                switchTabsCallback: setActiveTemplateIndex,
                openTabId: `${activeTemplateIndex}`,
            },
        },
        {
            title: t('settingsandtracking'),
            content: <SettingsAndTracking />,
            isClickable: !isDetailsScreen,
            status: (isDetailsScreen && 'active') || null,
            hasErrors: errors?.[CampaignErrorNames.SETTING_AND_TRACKING],
            errorsId: errors?.[CampaignErrorNames.SETTING_AND_TRACKING] ? uniqueId() : null,
        },
        ...(!isDetailsScreen
            ? [
                  {
                      title: t('save'),
                      content: <Footer />,
                      status: 'content-only' as StepStatus,
                  },
              ]
            : []),
    ];
};
